import { DEFAULT_PAGE_SIZE } from "@/shared/constants/pagination";
import { useUserRepository } from "@/shared/contexts/repository-context";
import { useHasGlobalPermission } from "@/shared/hooks/use-has-permission";
import { useSheetStore } from "@/shared/stores/sheet.store";
import {
  getGetUserListPaginatedQueryKey,
  useGetMeSuspense,
} from "@/spz-api/user/user";
import {
  GlobalPermission,
  SortDirection,
  type UserSortField,
} from "@spz/shared-domain";
import { Typography } from "@spz/ui/components/typography";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  type SortingState,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { InviteUserContent } from "../forms/invite-user";
import { useUserListColumns } from "../hooks/use-user-list-columns";

const useUserListRows = ({
  search,
  pagination,
  sorting,
}: {
  search?: string;
  pagination: {
    pageIndex: number;
    pageSize: number;
  };
  sorting: SortingState;
}) => {
  const userRepository = useUserRepository();
  const isPaginationValid = useMemo(() => {
    return (
      pagination.pageIndex >= 0 &&
      pagination.pageSize > 0 &&
      pagination.pageSize <= 100 &&
      sorting.length <= 1
    );
  }, [pagination, sorting]);

  const queryParams = {
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    search,
    sortField: sorting[0]?.id as UserSortField,
    sortDirection:
      sorting.length === 0
        ? undefined
        : sorting[0]?.desc
          ? SortDirection.DESC
          : SortDirection.ASC,
  };

  const { data, isLoading } = useQuery({
    queryKey: getGetUserListPaginatedQueryKey(queryParams),
    queryFn: async () => {
      return userRepository.getUsers(queryParams);
    },
    placeholderData: keepPreviousData,
    enabled: isPaginationValid,
  });

  return {
    rows: data?.items ?? [],
    rowCount: data?.total ?? 0,
    isLoading,
  };
};

export function useUserListViewModel() {
  const [search, setSearch] = useState("");
  const { openSheet } = useSheetStore();
  const { t } = useTranslation();

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  // Check if the user has the UsersManage permission
  const { data: me } = useGetMeSuspense();
  const hasUsersManagementPermission = useHasGlobalPermission(
    GlobalPermission.UsersManage,
    me,
  );

  const { rows, rowCount, isLoading } = useUserListRows({
    search,
    pagination,
    sorting,
  });

  const columns = useUserListColumns();

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount,
    initialState: {},
    state: {
      pagination,
      sorting,
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    enableColumnResizing: false,
  });

  const openInviteUserSheet = () => {
    openSheet({
      title: <Typography trans="invite_user" />,
      content: <InviteUserContent />,
    });
  };

  const onSearch = useCallback((search: string) => {
    setSearch(search);
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, []);

  return {
    t,
    hasUsersManagementPermission,
    table,
    onSearch,
    isLoading,
    openInviteUserSheet,
  };
}
